import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { catchError, debounceTime, forkJoin, of, Subscription, throwError } from 'rxjs';
import { MacroNutrientData } from 'src/app/models/macro-nutrient-data';
import { SyncService } from 'src/app/services/sync-service';
import { FieldDashboardDataService } from '../field-dashboard-data.service';
import {
  amountLabel, chartWindows, ChartWindows, escapeHtml, MacroElementKey, sameWindows, SeasonSplit, splitBySeason,
  tint, windowsSubtext
} from './season-period';

/** One stacked bar per element. The colour is the period (dark) part; the rest of the season is a tint of it. */
const ELEMENTS: { key: MacroElementKey; name: string; color: string }[] = [
  { key: 'n', name: 'N/Ha', color: '#5470c6' },
  { key: 'p', name: 'P/Ha', color: '#91cc75' },
  { key: 'k', name: 'K/Ha', color: '#fac858' },
  { key: 'ca', name: 'Ca/Ha', color: '#ee6666' },
  { key: 'mg', name: 'Mg/Ha', color: '#73c0de' },
  { key: 's', name: 'S/Ha', color: '#3ba272' }
];

@Component({
  selector: 'app-macronutrients',
  templateUrl: './macronutrients.component.html',
  styleUrls: ['./macronutrients.component.css'],
  standalone: false
})
export class MacronutrientsComponent implements OnInit, OnDestroy {
  @Input() fieldId: string = '';
  @Input() farmId: string;
  
  /** The picked period clipped to the season, as sent to the API; null when there is none. */
  @Input() fromDate: String;
  @Input() toDate: String;

  chartOption: EChartsOption = {};
  hasData: boolean = true;
  isLoading: boolean = true;

  /** Whole season (01/06 to 31/05), the full height of each bar. */
  macroNutrientData: MacroNutrientData = {
    cultivars: [],
    legends: [],
    n: [],
    p: [],
    k: [],
    ca: [],
    mg: [],
    s: []
  };
  /** The picked period within that season, the dark part of each bar; null when there is no period. */
  periodData: MacroNutrientData | null = null;
  windows: ChartWindows | null = null;

  private subscription: Subscription;

  constructor(
    private syncService: SyncService,
    private dataService: FieldDashboardDataService
  ) {
  }


  ngOnInit(): void {
    this.subscription = this.dataService.fieldData$.pipe(debounceTime(100)).subscribe(data => this.onFieldData(data));
  }

  ngOnDestroy(): void {
    // The data service outlives the page, so without this every visit leaves a subscriber behind.
    this.subscription?.unsubscribe();
  }

  onFieldData(data: any): void {
    if (!(data.fieldId && data.farmId && data.startDate && data.endDate)) return;
    const windows = chartWindows(data);
    if (!windows) return;
    if (this.fieldId === data.fieldId && sameWindows(this.windows, windows)) return;

    this.fieldId = data.fieldId;
    this.farmId = '2475';
    this.windows = windows;
    this.fromDate = windows.period?.from ?? null;
    this.toDate = windows.period?.to ?? null;
    this.populateData();
  }

  color = ['#FFB400', '#6355C7', '#5070dd', '#b6d634', '#505372', '#ff994d', '#0ca8df'];

  populateData() {
    this.isLoading = true;
    this.hasData = true;
    const { season, period } = this.windows;
    forkJoin({
      season: this.syncService.getMacroNutrientStats(this.fieldId, season.from, season.to),
      period: period ? this.syncService.getMacroNutrientStats(this.fieldId, period.from, period.to) : of(null)
    }).pipe(
      catchError(error => {
        console.error();
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.macroNutrientData = response.season;
      this.periodData = response.period;
      this.isLoading = false;
      this.loadChartData();
    })
  }

  loadChartData(): void {
    this.hasData = (this.macroNutrientData?.cultivars?.length ?? 0) > 0;
    if (!this.hasData) return;
    const cultivars = this.macroNutrientData.cultivars;
    // An element with nothing applied in the whole season gets no bar.
    const shown = ELEMENTS
      .map(e => ({ ...e, split: splitBySeason(this.macroNutrientData, this.periodData, e.key) }))
      .filter(e => e.split.total.some(v => v !== 0));

    const series: any[] = [];
    shown.forEach(e => {
      // Same name, so one legend entry hides both parts; same stack, so the period sits under the rest.
      series.push({
        id: `${e.name} period`, name: e.name, type: 'bar' as const, stack: e.name,
        data: e.split.period, itemStyle: { color: e.color }
      });
      series.push({
        id: `${e.name} rest`, name: e.name, type: 'bar' as const, stack: e.name,
        data: e.split.rest, itemStyle: { color: tint(e.color), borderColor: e.color, borderWidth: 1 }
      });
    });

    this.chartOption = {
      title: {
        text: 'N, P, K, Ca, Mg & S',
        subtext: this.windows ? windowsSubtext(this.windows) : '',
        left: 0,
        top: 10,
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params: any) => this.tooltipText(params, cultivars, shown)
      },
      legend: {
        data: shown.map(e => e.name),
        bottom: 0,
        itemGap: 25
      },
      grid: {
        left: '10%',
        right: '5%',
        bottom: '10%',
        top: '25%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: cultivars
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value}'
        }
      },
      series
    };
  }

  /** Per cultivar, each visible element as "N/Ha: 15,00 of 35,00": the period of the season. */
  private tooltipText(params: any, cultivars: string[], shown: { name: string; split: SeasonSplit }[]): string {
    const list: any[] = Array.isArray(params) ? params : [params];
    if (list.length === 0) return '';
    const index = list[0].dataIndex;
    const lines = [escapeHtml(cultivars[index])];
    shown.forEach(e => {
      // A legend-hidden element is absent from params.
      const param = list.find(p => p.seriesName === e.name);
      if (!param) return;
      lines.push(`${param.marker ?? ''}${e.name}: ${amountLabel(e.split.period[index])} of ${amountLabel(e.split.total[index])}`);
    });
    return lines.join('<br/>');
  }
}
